import CryptoJS from "crypto-js";
import { store } from "../redux";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { convertSnakeToTitleCase } from "./commonUtils";
import { pick } from "lodash";

type NestedObjectType = {
	[key in string]: NestedObjectType | any;
};

type FlattenedObjectType = {
	[key in string]: any;
};

export const BASE_API_URL = "/api/v1";

// WEBSOCKET CONNECTION
const WEBSOCKET_IP_PLACEHOLDER = "{WEBSOCKET_IP}";
// const WEBSOCKET_BASE_URL = `ws${window.location.protocol.replace("http", "")}//${WEBSOCKET_IP_PLACEHOLDER}:${
// 	process.env.REACT_APP_WEBSOCKET_PORT
// }/ws/server`;
const WEBSOCKET_BASE_URL = `ws${window.location.protocol.replace("http", "")}//${WEBSOCKET_IP_PLACEHOLDER}/ws/server`;

export const newDashSocket = (websocketIP?: string): WebSocket => {
	// if (!websocketIP) websocketIP = process.env.REACT_APP_WEBSOCKET_IP ?? "";
	if (!websocketIP) websocketIP = window.location.hostname;
	return new WebSocket(WEBSOCKET_BASE_URL.replace(WEBSOCKET_IP_PLACEHOLDER, websocketIP));
};

// API AUTH TOKEN
const API_REQUEST_HEADER_OPTIONS = {
	"X-Cue": "34db55e07f7b39df480284397f7f42ec",
	"X-Salt": "683239",
	"X-Alpha": "21",
	Accept: "application/json"
};

function getAuthToken() {
	const encryptionKey = String(process.env.REACT_APP_ENCRYPTION_KEY);
	const encryptedToken: string = store.getState().auth.user.token;

	const token = CryptoJS.AES.decrypt(encryptedToken, encryptionKey).toString(CryptoJS.enc.Utf8);
	return token ? `Token ${token}` : "";
}

export const getAPIHeaderOptions = (isAuthRequired = false) => {
	const token = getAuthToken();

	if (isAuthRequired && token) {
		return {
			...API_REQUEST_HEADER_OPTIONS,
			Authorization: token
		};
	}

	return API_REQUEST_HEADER_OPTIONS;
};

function flattenObject(object: NestedObjectType, parentKey?: string): FlattenedObjectType {
	let result: FlattenedObjectType = {};

	for (const [key, value] of Object.entries(object)) {
		if (typeof value === "object" && !Array.isArray(value)) {
			const flattenedValue = flattenObject(value, parentKey ? `${parentKey}.${key}` : key);

			result = {
				...result,
				...flattenedValue
			};
		} else {
			if (parentKey) {
				result[`${parentKey}.${key}`] = value;
			} else {
				result[key] = value;
			}
		}
	}

	return result;
}

function extractErrorMessage(error: FlattenedObjectType, parentKey?: string): string {
	if ("detail" in error) {
		error = pick(error, ["detail"]);
	} else if ("details" in error) {
		error = pick(error, ["details"]);
	}

	const errorValue = Object.values(error)[0] ?? "Something went wrong";
	const errorKey = Object.keys(error)[0];

	if (typeof errorValue === "object") {
		return extractErrorMessage(errorValue, errorKey);
	}

	const returnValue = String(errorValue);

	if (returnValue.toLowerCase().includes("this field")) {
		const replaceValue = parentKey ? parentKey : errorKey ? errorKey : "This field";
		return returnValue.replace(/this field/i, convertSnakeToTitleCase(replaceValue));
	} else if (returnValue.toLowerCase().includes("this value")) {
		const replaceValue = parentKey ? parentKey : errorKey ? errorKey : "This value";
		return returnValue.replace(/this value/i, convertSnakeToTitleCase(replaceValue));
	}

	return returnValue;
}

export const transformErrorResponse = (response: FetchBaseQueryError): FetchBaseQueryError => {
	if (response.data) {
		const error = flattenObject(response.data);
		response.data = extractErrorMessage(error);
	} else {
		response.data = "Something went wrong";
	}

	return response;
};
